import { Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun } from 'lucide-react';
import { useLocation } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { getAppEnvironment } from '@/lib/env';
import { useTheme } from '@/lib/theme/theme';

import { findActiveNavItem } from './navigation';
import { UserMenu } from './user-menu';

const environmentLabel = {
  development: 'Desarrollo',
  staging: 'Staging',
  production: 'Producción',
} as const;

interface TopbarProps {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileNav: () => void;
}

export function Topbar({ sidebarCollapsed, onToggleSidebar, onOpenMobileNav }: TopbarProps) {
  const { pathname } = useLocation();
  const activeItem = findActiveNavItem(pathname);
  const environment = getAppEnvironment();
  const { theme, toggle: toggleTheme } = useTheme();
  const themeLabel = theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro';

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-4 sm:px-6">
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        onClick={onOpenMobileNav}
        aria-label="Abrir navegación"
      >
        <Menu aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="hidden text-muted-foreground lg:inline-flex"
        onClick={onToggleSidebar}
        aria-label={sidebarCollapsed ? 'Expandir barra lateral' : 'Contraer barra lateral'}
        aria-expanded={!sidebarCollapsed}
      >
        {sidebarCollapsed ? (
          <PanelLeftOpen aria-hidden="true" />
        ) : (
          <PanelLeftClose aria-hidden="true" />
        )}
      </Button>

      <div className="mx-1 hidden h-5 w-px bg-border lg:block" aria-hidden="true" />

      <p className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
        {activeItem && (
          <activeItem.icon className="size-4 shrink-0 text-subtle-foreground" aria-hidden="true" />
        )}
        <span className="truncate">{activeItem?.label ?? 'SORAI ERP'}</span>
      </p>

      <div className="ml-auto flex items-center gap-2">
        {environment !== 'production' && (
          <Badge tone={environment === 'staging' ? 'warning' : 'info'}>
            {environmentLabel[environment]}
          </Badge>
        )}
        <Tooltip content={themeLabel} side="bottom">
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground"
            onClick={toggleTheme}
            aria-label={themeLabel}
            aria-pressed={theme === 'dark'}
          >
            {theme === 'dark' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
          </Button>
        </Tooltip>
        <UserMenu />
      </div>
    </header>
  );
}
